import React, { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  RichTextContent,
  EditableToken,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  digit: string;
  param: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

const WORD_NUMBERS: Record<string, string> = {
  studio: 'Studio',
  one: 'One',
  two: 'Two',
  three: 'Three',
  four: 'Four',
  five: 'Five',
};

/** Word-based category label, e.g. "One Bedroom", "Two Bedrooms", "Studio". */
function keyToWordLabel(key: string): string {
  if (key === 'studio') return 'Studio';
  const word = WORD_NUMBERS[key] || key;
  const noun = key === 'one' ? 'Bedroom' : 'Bedrooms';
  return `${word} ${noun}`;
}

/** Big display digit — "S" for studio, otherwise the bedroom count. */
function keyToDigit(key: string): string {
  if (key === 'studio') return 'S';
  const idx = BED_ORDER.indexOf(key);
  return idx > 0 ? String(idx) : key;
}

/**
 * Parse a price string ("1500", "$1,500", "1,500.00") to a positive integer,
 * or null if it isn't a usable number. AptSync/property data often stores
 * prices as strings with commas or currency symbols.
 */
function parsePrice(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (!cleaned) return null;
  const n = Math.round(parseFloat(cleaned));
  return isFinite(n) && n > 0 ? n : null;
}

/** Lowest `floorplan_price_min` across the loaded floorplans, or null. */
function minStartingPrice(floorplans: Floorplan[]): number | null {
  let min: number | null = null;
  for (const plan of floorplans) {
    const price = parsePrice(plan.floorplan_price_min);
    if (price != null && (min == null || price < min)) min = price;
  }
  return min;
}

const PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => ({
    key,
    label: keyToWordLabel(key),
    digit: keyToDigit(key),
    param: key,
  }));
}

// ============================================================================
// Category column sub-component
// ============================================================================

function CategoryColumn({ cat }: { cat: BedroomCategory }) {
  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className="reveal-fade-up fp-col group flex flex-col items-center text-center no-underline"
    >
      <span className="fp-col-digit leading-none text-[var(--vibe-color-primary,var(--vibe-color-text))] transition-transform duration-300 group-hover:-translate-y-1">
        {cat.digit}
      </span>
      <span className="fp-col-label mt-6 uppercase tracking-wide text-[var(--vibe-color-text)] whitespace-nowrap">
        {cat.label}
      </span>
      {/* Uniform underline: rendered as a separate fixed-width bar so every
          category's accent is the same length regardless of label text
          (STUDIO vs TWO BEDROOMS). */}
      <span className="fp-col-underline" aria-hidden="true" />
    </a>
  );
}

// ============================================================================
// Component
// ============================================================================

// Temporarily suppress the "Starting at $X,XXX" line in the subtitle while
// pricing is being finalized. Set back to false to bring it back.
const HIDE_STARTING_PRICE = true;

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  // "Starting at $X,XXX" derived from the lowest floorplan min price. Stays in
  // sync with editor overrides on the floorplans collection above.
  const startingPrice = useMemo(
    () => (floorplans?.length ? minStartingPrice(floorplans) : null),
    [floorplans],
  );
  const startingPriceLabel = useContentToken('floorplanslist.starting_price_label');
  const formattedStartingPrice = startingPrice != null ? PRICE_FORMATTER.format(startingPrice) : null;

  useRevealStyles();
  useComfyCtaStyles();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const hasCategories = categories.length > 0;

  // On sm+ we render each category into an equal-width CSS grid track so
  // the big digits (S / 1 / 2 …) sit at evenly spaced centers regardless
  // of how wide each label happens to be ("STUDIO" vs "TWO BEDROOMS").
  // The column count is data-driven, so we drive it through a CSS var
  // instead of a dynamic Tailwind class (JIT can't see runtime strings).
  const gridStyle = {
    '--fp-cols': categories.length || 1,
  } as React.CSSProperties;

  return (
    <section className="fp-section relative overflow-hidden bg-[var(--vibe-color-background)]">
      {/* Same opposing-diagonal clip as before; solid fill instead of photo. */}
      <div className="fp-section-bg absolute inset-0 bg-[#F5F6F7]" aria-hidden="true" />

      <div className="fp-section-inner relative z-10 max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-8">
        <div ref={headerRef} className="reveal-fade-up text-center mb-6 md:mb-10">
          <RichTextContent
            path="floorplanslist.title"
            label="Title"
            as="h2"
            className="fp-title font-heading font-bold uppercase text-[var(--vibe-color-text)] mb-4 leading-tight"
          />
          <p className="font-body text-base sm:text-lg text-[var(--vibe-color-text)] max-w-2xl mx-auto leading-relaxed [&_a]:text-[var(--vibe-color-text)] [&_a]:underline [&_a]:decoration-[var(--vibe-color-secondary)] [&_a]:underline-offset-2 [&_a:hover]:decoration-2">
            <RichTextContent
              path="floorplanslist.subtitle"
              label="Subtitle"
              as="span"
            />
            {/* "Starting at $X,XXX" temporarily hidden per client request.
                Flip HIDE_STARTING_PRICE back to false to restore it. */}
            {!HIDE_STARTING_PRICE && formattedStartingPrice && (
              <>
                {' '}
                <EditableToken
                  path="floorplanslist.starting_price_label"
                  label="Starting Price Label"
                  as="span"
                >
                  {startingPriceLabel}
                </EditableToken>{' '}
                <span className="fp-starting-price font-bold">
                  {formattedStartingPrice}
                </span>
              </>
            )}
          </p>
        </div>

        {hasCategories ? (
          <>
            <EditableCollectionBinding bindingPath="floorplanslist.items" label="Floor Plans">
              <div
                ref={gridRef}
                style={gridStyle}
                className="fp-grid grid grid-cols-1 gap-y-10 sm:gap-x-6 md:gap-x-10 lg:gap-x-12 mb-16 md:mb-20 mx-auto"
              >
                {categories.map((cat) => (
                  <CategoryColumn key={cat.key} cat={cat} />
                ))}
              </div>
            </EditableCollectionBinding>

            <div className="flex justify-center">
              <a
                data-reveal
                href="/floor-plans"
                className="reveal-fade-up comfy-cta fp-view-all-cta"
                // Width-matched to the Footer's Resident Portal button
                // (`.footer-cta-stack` uses `w-full max-w-[260px]` around a
                // `width: 100%` `.footer-cta-btn`). Keeping the two CTAs the
                // same physical width makes the button family read as one
                // system across the theme rather than sizing to label text.
                style={{ width: '100%', maxWidth: '260px' }}
              >
                View All Layouts
              </a>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'none' }} aria-hidden="true">
              <EditableCollectionBinding bindingPath="floorplanslist.items" label="Floor Plans">
                <span />
              </EditableCollectionBinding>
            </div>
            <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">Floor plans will be displayed here.</p>
            </div>
          </>
        )}
      </div>

      <style>{`
        /* Full-section band clipped to angled edges: top slopes down
           left→right, bottom slopes down right→left (same as before). */
        .fp-section-bg {
          clip-path: polygon(0 0, 100% 14%, 100% 86%, 0 100%);
          -webkit-clip-path: polygon(0 0, 100% 14%, 100% 86%, 0 100%);
        }
        @media (min-width: 768px) {
          .fp-section-bg {
            clip-path: polygon(0 0, 100% 18%, 100% 82%, 0 100%);
            -webkit-clip-path: polygon(0 0, 100% 18%, 100% 82%, 0 100%);
          }
        }

        /* Extra vertical padding on mobile so the title and CTA clear the
           clipped corners and stay fully on the grey band. */
        .fp-section-inner {
          padding-top: 11rem;
          padding-bottom: 11rem;
        }
        @media (min-width: 640px) {
          .fp-section-inner {
            padding-top: 9rem;
            padding-bottom: 9rem;
          }
        }

        /* Title: smaller mobile scale + tighter tracking so the first
           letter stays inside the content box on narrow viewports. */
        .fp-title {
          font-size: clamp(1.125rem, 4.2vw + 0.5rem, 2.75rem);
          letter-spacing: 0.02em;
          word-break: normal;
          overflow-wrap: break-word;
          max-width: 100%;
          padding-inline: 0.125rem;
          box-sizing: border-box;
        }
        @media (min-width: 640px) {
          .fp-title {
            letter-spacing: 0.06em;
          }
        }

        .fp-starting-price {
          color: var(--vibe-color-text);
        }

        /* Equal-width tracks on sm+ so every big digit lands at the same
           relative x-position within its cell. Without this, columns take
           their natural content width (label-driven) and the digits end up
           unevenly spaced. Falls back to a single stacked column on mobile. */
        @media (min-width: 640px) {
          .fp-grid {
            grid-template-columns: repeat(var(--fp-cols, 3), minmax(0, 1fr));
            /* Keep the row visually anchored — cap width so 2- or 3-item
               layouts don't stretch across the full 1100px container. */
            max-width: min(100%, calc(var(--fp-cols, 3) * 14rem));
          }
        }

        /* ── Icon (big bedroom-count digit) ────────────────────────────
           Aion type spec: Montserrat, Normal (400), 120px. Hover state
           uses the same face, weight, and size — only the transform /
           color transitions change. clamp() keeps the digit legible on
           narrow viewports while topping out at 120px on desktop. */
        .fp-col-digit {
          font-family: 'Montserrat', var(--vibe-font-heading, sans-serif);
          font-weight: 400;
          font-size: clamp(64px, 14vw, 120px);
        }
        @media (min-width: 1024px) {
          .fp-col-digit {
            font-size: 120px;
          }
        }
        .fp-col:hover .fp-col-digit {
          font-family: 'Montserrat', var(--vibe-font-heading, sans-serif);
          font-weight: 400;
          font-size: clamp(64px, 14vw, 120px);
        }
        @media (min-width: 1024px) {
          .fp-col:hover .fp-col-digit {
            font-size: 120px;
          }
        }

        /* ── Floor Plan Name (label) ───────────────────────────────────
           Aion type spec: Montserrat, Normal (400), 18px / 42px
           line-height. The 10px accent bar is rendered as a sibling
           (.fp-col-underline) below so its width is uniform across
           categories rather than sized to the label text. The label
           typography re-declares on hover to guarantee the hover state
           matches the resting state. */
        .fp-col-label {
          display: inline-block;
          font-family: 'Montserrat', var(--vibe-font-body, sans-serif);
          font-weight: 400;
          font-size: 18px;
          line-height: 42px;
          transition: color 0.25s ease;
        }
        .fp-col:hover .fp-col-label,
        .fp-col:focus-visible .fp-col-label {
          font-family: 'Montserrat', var(--vibe-font-body, sans-serif);
          font-weight: 400;
          font-size: 18px;
        }

        /* ── Uniform underline ──────────────────────────────────────────
           Fixed-width accent bar — primary-tinted for contrast on the
           light solid background while keeping 10px / 140px proportions. */
        .fp-col-underline {
          display: block;
          width: 140px;
          height: 10px;
          background-color: color-mix(
            in srgb,
            var(--vibe-color-primary, var(--vibe-color-text)) 45%,
            transparent
          );
          margin-top: 12px;
        }

        /* ── "View All Layouts" CTA typography override ─────────────────
           The shared .comfy-cta base uses the heading font at weight 500
           to match the Footer CTA family. In this section the client
           wants the button set in Montserrat at normal (400) weight, so
           we scope the override to this button only (via .fp-view-all-cta)
           and leave the shared family intact. */
        .fp-view-all-cta {
          font-family: 'Montserrat', var(--vibe-font-heading, var(--vibe-font-body, sans-serif));
          font-weight: 400;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Make Yourself at Home</p>',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Please select your desired layout below or browse our <a href="/gallery">photo gallery</a>.</p>',
    },
    'floorplanslist.starting_price_label': {
      label: 'Starting Price Label',
      type: 'text',
      defaultValue: 'Starting at',
    },
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
